<script setup>
import { computed, reactive, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { ARTICLE_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FullEditor from 'dashboard/components/widgets/WootWriter/FullEditor.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import TicketLabelBox from './TicketLabelBox.vue';
import TicketConversationPicker from './TicketConversationPicker.vue';

const props = defineProps({
  columns: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['save']);

const { t } = useI18n();

const PRIORITY_ICON = {
  urgent: 'i-woot-priority-urgent',
  high: 'i-woot-priority-high',
  medium: 'i-woot-priority-medium',
  low: 'i-woot-priority-low',
};

const isOpen = ref(false);
const editingId = ref(null);

const form = reactive({
  title: '',
  description: '',
  priority: '',
  ticketColumnId: '',
  assigneeId: '',
  conversationDisplayId: '',
  conversationLabel: '',
  labels: [],
});

const isEditing = computed(() => Boolean(editingId.value));
const isInvalid = computed(
  () => !form.title.trim() || !form.conversationDisplayId
);

const columnOptions = computed(() =>
  props.columns.map(c => ({ id: c.id, name: c.name, icon: 'i-lucide-columns-2' }))
);
const agentOptions = computed(() =>
  props.agents.map(a => ({
    id: a.id,
    name: a.name,
    thumbnail: a.thumbnail || a.avatar_url,
    availability_status: a.availability_status,
  }))
);
const priorityOptions = computed(() => [
  { id: '', name: t('TICKETS.PRIORITY.NONE') },
  ...['urgent', 'high', 'medium', 'low'].map(p => ({
    id: p,
    name: t(`TICKETS.PRIORITY.${p.toUpperCase()}`),
    icon: PRIORITY_ICON[p],
  })),
]);

const selectedColumn = computed(
  () => columnOptions.value.find(o => o.id === form.ticketColumnId) || {}
);
const selectedAgent = computed(
  () => agentOptions.value.find(o => o.id === form.assigneeId) || {}
);
const selectedPriority = computed(
  () => priorityOptions.value.find(o => o.id === form.priority) || {}
);

const onKeydown = e => {
  if (e.key === 'Escape') close();
};

const open = ({ ticket = {}, conversation = null } = {}) => {
  editingId.value = ticket.id || null;
  form.title = ticket.title || '';
  form.description = ticket.description || '';
  form.priority = ticket.priority || '';
  form.ticketColumnId = ticket.ticket_column_id || props.columns[0]?.id || '';
  form.assigneeId = ticket.assignee_id || '';
  form.conversationDisplayId = conversation || ticket.conversation_id || '';
  form.conversationLabel = ticket.conversation?.contact_name
    ? `#${ticket.conversation_id} · ${ticket.conversation.contact_name}`
    : '';
  form.labels = Array.isArray(ticket.labels) ? [...ticket.labels] : [];
  isOpen.value = true;
  document.addEventListener('keydown', onKeydown);
};
const close = () => {
  isOpen.value = false;
  document.removeEventListener('keydown', onKeydown);
};

const save = () => {
  if (isInvalid.value) return;
  emit('save', {
    id: editingId.value,
    ticket: {
      title: form.title.trim(),
      description: form.description.trim(),
      priority: form.priority || null,
      ticket_column_id: form.ticketColumnId || null,
      assignee_id: form.assigneeId || null,
      conversation_id: form.conversationDisplayId || null,
      labels: form.labels,
    },
  });
  close();
};

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9990] flex items-start justify-center p-4 overflow-y-auto bg-n-alpha-black1 backdrop-blur-[4px]"
      @click.self="close"
    >
      <div
        class="w-full max-w-lg my-auto rounded-xl bg-n-solid-1 shadow-xl outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-between px-4 py-2.5 border-b border-n-weak">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ isEditing ? t('TICKETS.DIALOG.EDIT_TITLE') : t('TICKETS.DIALOG.CREATE_TITLE') }}
          </h3>
          <Button icon="i-lucide-x" variant="ghost" color="slate" size="xs" @click="close" />
        </div>

        <div class="flex flex-col gap-3 p-4">
          <Input
            v-model="form.title"
            :label="t('TICKETS.DIALOG.TITLE_LABEL')"
            :placeholder="t('TICKETS.DIALOG.TITLE_PLACEHOLDER')"
            autofocus
          />

          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TICKETS.DIALOG.DESCRIPTION_LABEL') }}
            </label>
            <div class="max-h-40 overflow-y-auto rounded-lg outline outline-1 outline-n-weak">
              <FullEditor
                v-model="form.description"
                class="text-sm"
                :placeholder="t('TICKETS.DIALOG.DESCRIPTION_PLACEHOLDER')"
                :enabled-menu-options="ARTICLE_EDITOR_MENU_OPTIONS"
              />
            </div>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TICKETS.DIALOG.CONVERSATION_LABEL') }}
            </label>
            <TicketConversationPicker
              v-model="form.conversationDisplayId"
              :display-label="form.conversationLabel"
            />
          </div>

          <div class="grid grid-cols-3 gap-2">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.COLUMN_LABEL') }}</label>
              <MultiselectDropdown
                :options="columnOptions"
                :selected-item="selectedColumn"
                :has-thumbnail="false"
                :multiselector-title="t('TICKETS.DIALOG.COLUMN_LABEL')"
                :multiselector-placeholder="t('TICKETS.DIALOG.COLUMN_LABEL')"
                @select="item => (form.ticketColumnId = item.id)"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.PRIORITY_LABEL') }}</label>
              <MultiselectDropdown
                :options="priorityOptions"
                :selected-item="selectedPriority"
                :has-thumbnail="false"
                :multiselector-title="t('TICKETS.DIALOG.PRIORITY_LABEL')"
                :multiselector-placeholder="t('TICKETS.PRIORITY.NONE')"
                @select="item => (form.priority = item.id)"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-10">{{ t('TICKETS.DIALOG.ASSIGNEE_LABEL') }}</label>
              <MultiselectDropdown
                :options="agentOptions"
                :selected-item="selectedAgent"
                :multiselector-title="t('TICKETS.DIALOG.ASSIGNEE_LABEL')"
                :multiselector-placeholder="t('TICKETS.LIST.UNASSIGNED')"
                @select="item => (form.assigneeId = item.id)"
              />
            </div>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TICKETS.DIALOG.LABELS_LABEL') }}
            </label>
            <TicketLabelBox v-model="form.labels" />
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 px-4 py-3 border-t border-n-weak">
          <Button
            :label="t('TICKETS.DIALOG.CANCEL')"
            variant="faded"
            color="slate"
            size="sm"
            @click="close"
          />
          <Button
            :label="isEditing ? t('TICKETS.DIALOG.SAVE') : t('TICKETS.DIALOG.CREATE')"
            color="blue"
            size="sm"
            :disabled="isInvalid"
            @click="save"
          />
        </div>
      </div>
    </div>
  </TeleportWithDirection>
</template>
